/* Real HTTP API implementation of POD Client */

import axios, { AxiosInstance, AxiosResponse, CancelToken } from 'axios';
import dayjs from 'dayjs';
import { getApiConfig } from '../../environment';
import { IPodClient } from './IPodClient';
import {
  DetailedResponse,
  ItemConversion,
  PodConfigurationModel,
  PredictedDemandModel,
  StoreConfiguration,
} from './podTypes';

// Clerk type declaration
declare global {
  interface Window {
    Clerk?: {
      session?: {
        getToken?: ({ template }: { template: string }) => Promise<string | null>;
      };
    };
  }
}

const apiConfig = getApiConfig();

/* ---- POD Client (Real API) ---- */
export class PodClientApi implements IPodClient {
  private static instance: PodClientApi;
  private readonly axiosInstance: AxiosInstance;

  private constructor() {
    this.axiosInstance = axios.create({
      baseURL: apiConfig.apiBaseUrl,
    });

    this.axiosInstance.interceptors.request.use(
      async config => {
        const token = await window.Clerk?.session?.getToken?.({ template: 'Orderly_default' });
        if (!token) {
          throw new Error('Authorization token is missing.');
        }

        config.headers['Accept'] = 'application/json';
        config.headers['Authorization'] = `Bearer ${token}`;
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.headers['Expires'] = '-1';
        config.headers['If-Modified-Since'] = 'Thu, 13 Feb 1985 13:40:01 GMT';
        config.headers['Cache-Control'] = 'no-cache';
        config.headers['Pragma'] = 'no-cache';

        return config;
      },
      error => {
        return Promise.reject(error);
      },
    );
  }

  public static getInstance(): PodClientApi {
    if (!PodClientApi.instance) {
      PodClientApi.instance = new PodClientApi();
    }
    return PodClientApi.instance;
  }

  /* ---- Configuration Methods ---- */

  public async getConfigurations(
    configurationLevel: number,
    franchiseName?: string,
    storeNumber?: string,
    cancelToken?: CancelToken,
  ): Promise<PodConfigurationModel[]> {
    try {
      const params = {
        configurationLevel,
        franchiseName,
        storeNumber,
      };

      const response: AxiosResponse<PodConfigurationModel[]> = await this.axiosInstance.get('getconfigurations', {
        params,
        cancelToken,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getStoreConfiguration(
    franchiseName: string,
    storeNumber: string,
    cancelToken?: CancelToken,
  ): Promise<StoreConfiguration> {
    try {
      const params = {
        franchiseName,
        storeNumber,
      };

      const response: AxiosResponse<StoreConfiguration> = await this.axiosInstance.get('getstoreconfiguration', {
        params,
        cancelToken,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async create(model: PodConfigurationModel, cancelToken?: CancelToken): Promise<DetailedResponse> {
    try {
      const response: AxiosResponse = await this.axiosInstance.post('createconfiguration', model, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async update(model: PodConfigurationModel, cancelToken?: CancelToken): Promise<DetailedResponse> {
    try {
      const response: AxiosResponse = await this.axiosInstance.put('updateconfiguration', model, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async delete(id: number, cancelToken?: CancelToken): Promise<DetailedResponse> {
    try {
      const response: AxiosResponse = await this.axiosInstance.delete(`deleteconfiguration/${id}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  /* ---- Prediction Methods ---- */

  public async getPredictions(
    franchiseName: string,
    storeNumber: string,
    availableDates: string[],
    cancelToken?: CancelToken,
  ): Promise<PredictedDemandModel[]> {
    try {
      const dates = availableDates
        .map(x => {
          const parsedDate = dayjs(x, 'DD-MM-YYYY');
          return parsedDate.format('YYYY-MM-DD');
        })
        .join(',');
      const params = {
        franchiseName,
        storeNumber,
        dates,
      };

      const response: AxiosResponse<PredictedDemandModel[]> = await this.axiosInstance.get('getpredictions', {
        params,
        cancelToken,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getPredictionsForItems(
    franchiseName: string,
    storeNumber: string,
    deliveryDates: string[],
    itemNumbersList: string[],
    conversions?: ItemConversion[],
    cancelToken?: CancelToken,
  ): Promise<PredictedDemandModel[]> {
    try {
      const dates = deliveryDates.map(x => {
        const parsedDate = dayjs(x, 'DD-MM-YYYY');
        return parsedDate.format('YYYY-MM-DD');
      });

      const requestBody = {
        franchiseName,
        storeNumber,
        dates,
        itemNumbers: itemNumbersList,
        conversions: conversions || [],
      };

      const response: AxiosResponse<PredictedDemandModel[]> = await this.axiosInstance.post(
        'getpredictionsforitems',
        requestBody,
        { cancelToken },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }
}
